import React, { useState, useEffect } from "react";
import { IndianRupee, Clock, CheckCircle, CreditCard, Banknote } from "lucide-react";
import { StatsCard, AppBadge, EmptyState } from "../../../ui";
import { DetailRow, DetailSection } from "../../../ui/DetailRow";
import apiClient from "../../../../utils/apiClient";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

const EarningsTab = ({ institute }) => {
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get(`/sa/payouts/pending/${institute._id}`);
        setPending(data);
      } catch {
        // no pending
      } finally {
        setLoading(false);
      }
    })();
  }, [institute._id]);

  if (loading) {
    return <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>;
  }

  const paymentType = institute.paymentType || "own_gateway";
  const bank = institute.bankDetails;

  return (
    <div className="sa-detail-tab">
      <DetailSection title="Payment Configuration">
        <DetailRow
          icon={CreditCard}
          label="Payment Type"
          value={
            <AppBadge variant="outline" color={paymentType === "lms_payment" ? "blue" : "emerald"}>
              {paymentType === "lms_payment" ? "LMS Payment (Platform Gateway)" : "Own Gateway"}
            </AppBadge>
          }
        />
      </DetailSection>

      {paymentType === "lms_payment" && (
        <>
          <DetailSection title="Bank Details">
            {bank?.accountNumber ? (
              <>
                <DetailRow icon={Banknote} label="Account Holder" value={bank.accountHolderName || "—"} />
                <DetailRow label="Account Number" value={bank.accountNumber} />
                <DetailRow label="IFSC Code" value={bank.ifscCode || "—"} />
                <DetailRow label="Bank Name" value={bank.bankName || "—"} />
              </>
            ) : (
              <div style={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>
                Bank details not provided by institute.
              </div>
            )}
          </DetailSection>

          {pending?.totals ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem", marginTop: "0.5rem" }}>
                <StatsCard title="Pending Payout" value={formatCurrency(pending.totals.netPayable)} icon={Clock} iconColor="amber" />
                <StatsCard title="Total Collected" value={formatCurrency(pending.totals.totalCollected)} icon={IndianRupee} iconColor="blue" />
                <StatsCard title="Commission" value={formatCurrency(pending.totals.totalCommission)} icon={IndianRupee} iconColor="rose" />
              </div>

              {pending.transactions?.length > 0 && (
                <DetailSection title={`Pending Transactions (${pending.transactions.length})`}>
                  <div style={{ overflowX: "auto" }}>
                    <table className="payout-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
                      <thead>
                        <tr>
                          <th style={{ padding: "0.5rem", borderBottom: "1px solid var(--border-primary)" }}>Student</th>
                          <th style={{ padding: "0.5rem", borderBottom: "1px solid var(--border-primary)" }}>Amount</th>
                          <th style={{ padding: "0.5rem", borderBottom: "1px solid var(--border-primary)" }}>Commission</th>
                          <th style={{ padding: "0.5rem", borderBottom: "1px solid var(--border-primary)" }}>Net</th>
                          <th style={{ padding: "0.5rem", borderBottom: "1px solid var(--border-primary)" }}>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pending.transactions.slice(0, 10).map((t) => (
                          <tr key={t._id}>
                            <td style={{ padding: "0.5rem" }}>{t.studentId?.name || "—"}</td>
                            <td style={{ padding: "0.5rem" }}>{formatCurrency(t.amount)}</td>
                            <td style={{ padding: "0.5rem" }}>{formatCurrency(t.commissionAmount)}</td>
                            <td style={{ padding: "0.5rem" }}>{formatCurrency(t.netPayable)}</td>
                            <td style={{ padding: "0.5rem", fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                              {new Date(t.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </DetailSection>
              )}
            </>
          ) : (
            <EmptyState icon={<CheckCircle size={28} />} title="No Pending Payouts" description="All settlements are up to date for this institute." />
          )}
        </>
      )}
    </div>
  );
};

export default EarningsTab;
